import React, { useState, useEffect, useContext, useRef } from "react";
import { StyleSheet, ScrollView, Text, TouchableOpacity, Modal as RNModal, Pressable } from "react-native";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../../components/custom/Modal";
import Spinner from "../../../components/shared/Spinner";
import { getPmxInventory } from "../../../services/inventoryService";

import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersPMXOverview,
  mdHeadersPMXOverview,
  smHeadersPMXOverview,
} from "../utils/pmxInventoryHeaders";
import { pmxOverviewModel } from "../../../models/reportsModel";
import { validSignedURL } from "../../../utils/validateSignedUrl";
import { handleTableLoader } from "../../../utils/common";
import { useSelector } from "react-redux";
import { userClaimInformation } from "../../../models/usersModel";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { sortColumns } from "../../../utils/sortColumns";
import AsyncStorage from "@react-native-async-storage/async-storage";
// @ts-ignore
import { Feather } from "@expo/vector-icons";

export default function PmxInventoryReportScreen({ route, navigation }: any) {
  const { isLargeDevice, isMediumDevice } = useContext(
    DeviceOrientationContext
  );

  const { pmxInventoryReport, isPmxInventoryReportListLoaded } = useSelector(
    (state: any) => state.reports
  );
  const { userInfo } = useSelector((state: any) => state.configurations);

  const [selected, setSelected] = useState<pmxOverviewModel>(
    {} as unknown as pmxOverviewModel
  );

  const [pmxPageLevelList, setPmxPageLevelList] = useState<
    Array<pmxOverviewModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersPMXOverview
    );
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("pmx_inventory_report_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available headers based on device size
  const getAllHeaders = () => {
    return isLargeDevice ? headersPMXOverview : isMediumDevice ? mdHeadersPMXOverview : smHeadersPMXOverview;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(([key]) => !hiddenColumns.includes(key));
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, isLargeDevice, isMediumDevice]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("pmx_inventory_report_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      const searchedList: Array<pmxOverviewModel> = filterBasedOnAnyKey(
        combineDuplicatedData(pmxInventoryReport),
        searchText
      );
      setPmxPageLevelList(searchedList);
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedPmxList: Array<pmxOverviewModel> = sortColumns(
      pmxPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setPmxPageLevelList(_sortedPmxList);
  };

  const combineDuplicatedData = (data: Array<pmxOverviewModel>) => {
    let holder: Record<
      string,
      Record<string, string | number | pmxOverviewModel>
    > = {};

    data.forEach((pmx: pmxOverviewModel) => {
      let key = `${pmx.ItemCode} - ${pmx.Location}`;
      if (holder.hasOwnProperty(key)) {
        if (
          holder[key].ItemName === pmx.ItemName &&
          holder[key].Location === pmx.Location
        ) {
          holder[key] = {
            InvntryUom: pmx.InvntryUom,
            ItemCode: pmx.ItemCode,
            ItemName: pmx.ItemName,
            Location: pmx.Location,
            QuantityUom:
              (holder[key].QuantityUom as number) + (pmx.QuantityUom as number),
            QuantityUom2:
              (holder[key].QuantityUom2 as number) +
              (pmx.QuantityUom2 as number),
            Uom2: pmx.Uom2,
            Warehouse: pmx.Warehouse,
          };
        } else {
          holder[key] = { ...pmx };
        }
      } else {
        holder[key] = { ...pmx };
      }
    });

    const filteredPmxList: Array<pmxOverviewModel> = [];
    for (let prop in holder) {
      filteredPmxList.push({
        InvntryUom: holder[prop].InvntryUom,
        ItemCode: holder[prop].ItemCode,
        ItemName: holder[prop].ItemName,
        Location: holder[prop].Location,
        QuantityUom: holder[prop].QuantityUom,
        QuantityUom2: holder[prop].QuantityUom2,
        Uom2: holder[prop].Uom2,
        Warehouse: holder[prop].Warehouse,
      } as pmxOverviewModel);
    }

    return filteredPmxList;
  };

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (pmxPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();
        getPmxInventory(
          "",
          _userInfo.team,
          _userInfo.zone_info || "",
          tableHeaders
        );
      }
    }

    if (Object.keys(selected).length !== 0) {
      validSignedURL(
        selected.ItemCode,
        "label",
        setImageLoading,
        setSelectedImage
      );
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [selected, isRefresh]);

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    setPmxPageLevelList(combineDuplicatedData(pmxInventoryReport));

    setTimeout(() => {
      fadeTableLoader();
    }, 2000);
  }, [pmxInventoryReport]);

  const ModalContent = () => {
    const _excludedKeys = ["ItemCode", "InvntryUom", "Uom2"];

    const _orderedKeys = [
      "ItemName",
      "Warehouse",
      "Location",
      "QuantityUom",
      "QuantityUom2",
    ];

    const _specialLabels = {
      QuantityUom: {
        oldLabel: "QuantityUom",
        newLabel: "Piece",
      },
      QuantityUom2: {
        oldLabel: "QuantityUom2",
        newLabel: "Box",
      },
    };

    return (
      <Modal.ModalContent
        title={selected.ItemCode || ""}
        icon="folder"
        uri={selectedImage}
        loading={isImageLoading}
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headersPMXOverview}
            orderedKeys={_orderedKeys}
            specialLabels={_specialLabels}
          />
        }
      />
    );
  };

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("PmxInventoryDetailsReportScreen", { order });
  };

  return (
    <ScrollView style={{ backgroundColor: "white" }}>
      <View style={[styles.container, { backgroundColor: "#f2f2f2" }]}>
        <View
          style={[
            styles.containerContent,
            {
              width: "100%",
            },
          ]}
        >
          {isPmxInventoryReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${pmxPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={pmxPageLevelList}
                fileName={"inventory_overview_report"}
                extraControls={(
                  <TouchableOpacity
                    ref={settingsBtnRef}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      backgroundColor: "#F3F4F6",
                      justifyContent: "center",
                      alignItems: "center",
                      borderWidth: 1,
                      borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                    }}
                    onPress={() => {
                      if (settingsBtnRef.current) {
                        settingsBtnRef.current.measureInWindow(
                          (x: number, y: number, width: number, height: number) => {
                            setSettingsBtnPos({ x: x || 0, y: y || 0, width: width || 0, height: height || 0 });
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        );
                      } else {
                        setColumnSettingsOpen(!columnSettingsOpen);
                      }
                    }}
                  >
                    <Feather
                      name="settings"
                      size={15}
                      color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                    />
                  </TouchableOpacity>
                )}
              />
              <DataTableWithPagination
                headers={tableHeaders}
                rows={pmxPageLevelList}
                itemsPerPage={100}
                useModal={true}
                isDataLoading={isSearched}
                isRowsExpandable={false}
                setModalVisible={setModalVisible}
                setSelected={setSelected}
                isDetail={true}
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
                operationActions={navigateToOrder}
              />
              {columnSettingsOpen && (
                <RNModal
                  transparent={true}
                  visible={columnSettingsOpen}
                  animationType="fade"
                  onRequestClose={() => setColumnSettingsOpen(false)}
                >
                  <Pressable
                    style={{
                      flex: 1,
                      backgroundColor: "rgba(0,0,0,0.1)",
                    }}
                    onPress={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={{
                        position: "absolute",
                        top: settingsBtnPos.y + settingsBtnPos.height + 6,
                        left: settingsBtnPos.x - 180 + 40,
                        backgroundColor: "#fff",
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: "#E2E8F0",
                        paddingVertical: 10,
                        minWidth: 220,
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: 8 },
                        shadowOpacity: 0.15,
                        shadowRadius: 20,
                        elevation: 10,
                      }}
                      onPress={(e) => e.stopPropagation()}
                    >
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: "700",
                          color: "#6B7280",
                          paddingHorizontal: 16,
                          paddingBottom: 8,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Show / Hide Columns
                      </Text>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                      {/* Select All */}
                      <TouchableOpacity
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          paddingVertical: 8,
                          paddingHorizontal: 16,
                        }}
                        onPress={() => {
                          const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                          if (hiddenColumns.length === 0) {
                            const updated = [...allKeys];
                            setHiddenColumns(updated);
                            AsyncStorage.setItem("pmx_inventory_report_hidden_columns", JSON.stringify(updated));
                          } else {
                            setHiddenColumns([]);
                            AsyncStorage.setItem("pmx_inventory_report_hidden_columns", JSON.stringify([]));
                          }
                        }}
                      >
                        <View
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 1.5,
                            borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                            backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 10,
                          }}
                        >
                          {hiddenColumns.length === 0 && (
                            <Feather name="check" size={13} color="#fff" />
                          )}
                        </View>
                        <Text
                          style={{
                            fontSize: 14,
                            color: "#111827",
                            fontWeight: "700",
                          }}
                        >
                          Select All
                        </Text>
                      </TouchableOpacity>
                      <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                      <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                        {Object.entries(getAllHeaders()).map(([key, value]) => {
                          if (key === "") return null;
                          const isVisible = !hiddenColumns.includes(key);
                          return (
                            <TouchableOpacity
                              key={key}
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                paddingVertical: 8,
                                paddingHorizontal: 16,
                              }}
                              onPress={() => toggleColumn(key)}
                            >
                              <View
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 1.5,
                                  borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                  backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 10,
                                }}
                              >
                                {isVisible && (
                                  <Feather name="check" size={13} color="#fff" />
                                )}
                              </View>
                              <Text
                                style={{
                                  fontSize: 14,
                                  color: isVisible ? "#111827" : "#9CA3AF",
                                  fontWeight: isVisible ? "600" : "400",
                                }}
                              >
                                {(value as any).name}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </Pressable>
                  </Pressable>
                </RNModal>
              )}
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    // marginTop: 20
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});
